import { type FunctionComponent, useEffect, useRef } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { type LexicalEditor } from "lexical";

import { type RichTextEditorSerializeAs } from "../RichTextEditorProps";
import { hydrateComponents, hydrateHtml, hydrateMarkdown } from "../utils";

export interface HydrateInitialValuePluginDefs {
  readonly initialValue: string | null | undefined;
  readonly serializeAs: RichTextEditorSerializeAs;
}

const hydrateFromLexicalJson = (editor: LexicalEditor, json: string): void => {
  editor.setEditorState(editor.parseEditorState(JSON.parse(json)));
};

export const HydrateInitialValuePlugin: FunctionComponent<HydrateInitialValuePluginDefs> = ({ initialValue, serializeAs }) => {
  const [editor] = useLexicalComposerContext();
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (hasHydratedRef.current || !initialValue) return;
    hasHydratedRef.current = true;
    if (serializeAs === "lexical-json") {
      hydrateFromLexicalJson(editor, initialValue);
      return;
    }
    editor.update(() => {
      switch (serializeAs) {
        case "html":
          hydrateHtml(editor, initialValue);
          return;
        case "markdown":
          hydrateMarkdown(initialValue);
          return;
        case "components":
          hydrateComponents(initialValue);
          return;
        default:
          return;
      }
    });
  }, [editor, initialValue, serializeAs]);

  return null;
};
